/**
 * THE COIN MESH — a small, detailed, hand-built gold coin.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS CODE AND NOT A GLB
 *
 * The coin is the single most size-constrained object in the game. It is
 * instanced up to `TUNING.coins.poolSize` (96) times, it is ~0.5 m across, and
 * after the 2026-08-02 playtest it lives in the bottom band of the road where
 * it must NOT occlude the traffic behind it. What has to read at 15-30 m is the
 * SILHOUETTE and the way light breaks across the relief — a disc with a proud
 * rim and a sunken field catches the sun on the rim and shades the field, which
 * is what makes a small gold blob read as a coin rather than as a dot.
 *
 * A generated mesh WAS produced and MEASURED before this file was written (the
 * concept is committed at `assets/concepts/coin.png`; see ASSET_PIPELINE.md for
 * the job and the review renders). `multi_image_to_3d` returned a genuinely
 * good 2,864-tri coin — and decimating it to the 600-tri instancing budget
 * shredded it: torn rim, holes through the field, the emblem gone. That is
 * inherent, not bad luck. The generator reconstructs a SHELL, so its triangles
 * are spread evenly over a surface that is mostly flat disc; the budget cannot
 * pay for both the 24-sided silhouette and the relief, and the decimator spends
 * it on neither. It also ships the fully-averaged split normals documented in
 * ASSET_PIPELINE.md, which smear the rim step into a gradient before decimation
 * even starts.
 *
 * Built here instead, every triangle is spent deliberately: the step between
 * rim and field is exact, the normals are hard where they should be hard, the
 * count is known at compile time (pinned by coinGeometry.test.ts), and the coin
 * is the same candy-gold plastic material as everything else in the scene with
 * no 2.2 MB texture to fight it.
 *
 * ---------------------------------------------------------------------------
 * THE PARTS (all merged into ONE BufferGeometry — the InstancedMesh must stay
 * a single draw call)
 *
 *   edge     a MILLED rim: an open cylinder whose radius alternates in and out
 *            by `MILL_DEPTH` every other segment, so the edge-on silhouette is
 *            a ridged bar and a spinning coin twinkles as the ridges catch the
 *            sun. Free — it is the same triangles a smooth cylinder would use.
 *   rim      a flat annulus at full thickness: the proud outer ring.
 *   wall     the short inner cylinder that drops from the rim to the field.
 *   field    the sunken face disc the emblem sits on.
 *   bolt     an extruded lightning-bolt emblem, proud of the field.
 *
 * rim / wall / field / bolt are built TWICE, once per face, so the coin is
 * correct from either side (it spins a full turn, so both are seen).
 *
 * The whole thing is authored with the disc axis along +Y and rotated onto Z at
 * the end, so the FACE points down the road at the player — the orientation
 * render/coins.ts then spins about the vertical axis.
 */

import {
  BufferGeometry,
  CircleGeometry,
  CylinderGeometry,
  ExtrudeGeometry,
  RingGeometry,
  Shape,
  type BufferAttribute,
} from "three/webgpu";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

/**
 * Coin diameter (m).
 *
 * OWNER PLAYTEST 2026-08-02: at 0.85 m the coin was "way too big, can't see
 * past the traffic" — a chain of hip-height discs is a picket fence across the
 * one thing the chain exists to advertise. The previous sizing argument (a
 * 0.55 m coin is an 8 px speck) was answered at the WRONG DISTANCE: nothing is
 * decided at 50 m. The decision distance is 15-30 m, where 0.5 m subtends
 * 0.95-1.9 deg = 11-23 px in the 78 deg / 960 px portrait viewport — a clearly
 * readable target — and the far end of a chain is ALLOWED to be a glint.
 */
export const COIN_DIAMETER = 0.5;
/** Coin thickness (m) — chunky, so the edge-on silhouette is a bar, not a line. */
export const COIN_THICKNESS = 0.12;

/** Radial segments. 24 keeps the disc round at 23 px and the mill visible. */
const SEGMENTS = 24;
/** How far the milled ridges cut into the edge (m). */
const MILL_DEPTH = 0.01;
/** Radius of the sunken field, as a fraction of the coin radius. */
const FIELD_FRACTION = 0.78;
/** How deep the field sits below the rim face (m). */
const FIELD_DEPTH = 0.02;
/**
 * How far the emblem stands proud of the field (m).
 *
 * MUST stay below FIELD_DEPTH: the emblem is allowed to fill the recess but
 * never to break the rim plane, or the coin loses the clean disc silhouette
 * that is the only thing legible at 20 px.
 */
const EMBLEM_HEIGHT = 0.016;
/** Chamfer on the emblem outline (m) — see `boltGeometry`, this is what reads. */
const EMBLEM_BEVEL = 0.005;
/** Emblem half-height (m); the bolt shape below is authored in ±1 units. */
const EMBLEM_SCALE = 0.145;

/**
 * The lightning-bolt emblem, authored in a ±1 box (x right, y up) and scaled by
 * `EMBLEM_SCALE`. Six points: the classic down-right zigzag.
 *
 * CHUNKY ON PURPOSE. The first pass used a narrow bolt and it rendered as a
 * scratch — a 2 px-wide stroke has no interior for the light to break across,
 * so the relief simply vanished. These lobes are ~0.75 units wide at the waist,
 * which is what makes the emblem read as EMBOSSED rather than as a scribe line.
 */
const BOLT_POINTS: readonly (readonly [number, number])[] = [
  [0.42, 1.0],
  [-0.62, 0.06],
  [-0.1, 0.06],
  [-0.42, -1.0],
  [0.62, -0.06],
  [0.1, -0.06],
];

/**
 * Normalise a part so `mergeGeometries` will accept it: exactly the
 * (position, normal, uv) attribute set, no groups, and NON-INDEXED.
 *
 * The de-indexing is the load-bearing bit. The primitives here disagree —
 * Cylinder/Ring/Circle arrive indexed, ExtrudeGeometry does not — and
 * `mergeGeometries` silently returns null on that mismatch rather than
 * reconciling it. At this size the cost is nil (a few hundred extra vertices on
 * a mesh that is one instanced draw), and it is done AFTER the milled edge's
 * `computeVertexNormals`, so the smooth ridge normals survive intact.
 */
function trim(g: BufferGeometry): BufferGeometry {
  for (const name of Object.keys(g.attributes)) {
    if (name !== "position" && name !== "normal" && name !== "uv") {
      g.deleteAttribute(name);
    }
  }
  g.clearGroups();
  if (!g.getIndex()) return g;
  const flat = g.toNonIndexed();
  g.dispose();
  return flat;
}

/** The extruded bolt, centred on the origin in XY, extruding toward +Z. */
function boltGeometry(): BufferGeometry {
  const shape = new Shape();
  const [x0, y0] = BOLT_POINTS[0];
  shape.moveTo(x0 * EMBLEM_SCALE, y0 * EMBLEM_SCALE);
  for (let i = 1; i < BOLT_POINTS.length; i++) {
    const [x, y] = BOLT_POINTS[i];
    shape.lineTo(x * EMBLEM_SCALE, y * EMBLEM_SCALE);
  }
  shape.closePath();
  // THE BEVEL IS THE WHOLE EMBLEM. A flat-topped extrusion standing on a flat
  // field is INVISIBLE under a directional sun: the emblem's top face and the
  // field beneath it share a normal, so they shade identically and only the
  // thin vertical walls survive — the first pass rendered the bolt as a scribe
  // line for exactly this reason. A chamfer gives the outline a band of
  // in-between normals that catches the sun and darkens on the shadow side,
  // which is what makes the shape read as struck metal.
  //
  // curveSegments 1 / bevelSegments 1: the outline is straight-edged and the
  // chamfer only needs one ring, so neither buys anything above 1.
  const g = new ExtrudeGeometry(shape, {
    depth: EMBLEM_HEIGHT - 2 * EMBLEM_BEVEL,
    bevelEnabled: true,
    bevelThickness: EMBLEM_BEVEL,
    bevelSize: EMBLEM_BEVEL * 1.6,
    bevelOffset: 0,
    bevelSegments: 1,
    curveSegments: 1,
  });
  return trim(g);
}

/**
 * Build the coin. Returns ONE non-indexed-safe merged BufferGeometry whose face
 * points along ±Z, centred on the origin, `COIN_DIAMETER` across.
 *
 * @param diameter   override the diameter (tests / tuning experiments).
 * @param thickness  override the thickness.
 */
export function buildCoinGeometry(
  diameter: number = COIN_DIAMETER,
  thickness: number = COIN_THICKNESS,
): BufferGeometry {
  const R = diameter / 2;
  const H = thickness / 2;
  const rField = R * FIELD_FRACTION;
  const yField = H - FIELD_DEPTH;

  const parts: BufferGeometry[] = [];

  // --- the milled edge ---------------------------------------------------
  // An OPEN cylinder (the caps are the rim annuli below). Every other radial
  // column is pulled in by MILL_DEPTH: CylinderGeometry lays its side vertices
  // out column-major, (SEGMENTS + 1) columns of 2 rows, so the column index is
  // just floor(i / 2).
  const edge = new CylinderGeometry(R, R, thickness, SEGMENTS, 1, true);
  {
    const pos = edge.attributes.position as BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      if (Math.floor(i / 2) % 2 === 0) continue;
      const k = (R - MILL_DEPTH) / R;
      pos.setX(i, pos.getX(i) * k);
      pos.setZ(i, pos.getZ(i) * k);
    }
    pos.needsUpdate = true;
    // The scallop moved vertices, so the authored normals are stale — and this
    // is the one part whose normals must follow the ridges for them to catch
    // any light at all.
    edge.computeVertexNormals();
  }
  parts.push(trim(edge));

  // --- the two faces -----------------------------------------------------
  for (const side of [1, -1] as const) {
    // rim: the proud annulus at full thickness.
    const rim = new RingGeometry(rField, R, SEGMENTS, 1);
    rim.rotateX(side === 1 ? -Math.PI / 2 : Math.PI / 2);
    rim.translate(0, side * H, 0);
    parts.push(trim(rim));

    // wall: drops from the rim face down to the field.
    const wall = new CylinderGeometry(rField, rField, FIELD_DEPTH, SEGMENTS, 1, true);
    wall.translate(0, side * (H - FIELD_DEPTH / 2), 0);
    parts.push(trim(wall));

    // field: the sunken disc the emblem sits on.
    const field = new CircleGeometry(rField, SEGMENTS);
    field.rotateX(side === 1 ? -Math.PI / 2 : Math.PI / 2);
    field.translate(0, side * yField, 0);
    parts.push(trim(field));

    // bolt: extruded toward +Z in its own space, so rotate the extrusion axis
    // onto ±Y and sit its BASE on the field.
    const bolt = boltGeometry();
    if (side === 1) {
      bolt.rotateX(-Math.PI / 2);
    } else {
      bolt.rotateX(Math.PI / 2);
      // The mirrored face reads the emblem from behind; flip it back so the
      // bolt zigzags the same way from either side.
      bolt.rotateY(Math.PI);
    }
    bolt.translate(0, side * yField, 0);
    parts.push(bolt);
  }

  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (!merged) throw new Error("coinGeometry: merge failed (attribute mismatch)");
  // The face must point down the road at the player; render/coins.ts then spins
  // this about the vertical axis.
  merged.rotateX(Math.PI / 2);
  merged.computeBoundingSphere();
  return merged;
}

/** Triangle count of a geometry — used by the budget test. */
export function triangleCount(g: BufferGeometry): number {
  const index = g.getIndex();
  return (index ? index.count : g.attributes.position.count) / 3;
}
